import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  alpha,
  Chip,
} from '@mui/material';
import { LayoutGrid, List, Plus, Search, Bot, Sparkles } from 'lucide-react';
import BotCard from './BotCard';
import EmptyState from '../../../components/common/EmptyState';
import DataTable from '../../../components/data-display/DataTable';

const BotList = ({
  bots = [],
  loading = false,
  onCreateBot,
  onEditBot,
  onDeleteBot,
  onSettingsBot,
  onViewBot,
}) => {
  const resolveActive = (bot) => {
    if (typeof bot?.isActive === 'boolean') return bot.isActive;
    if (bot?.b_active !== undefined) return Number(bot.b_active) === 1;
    if (bot?.b_status !== undefined) return Number(bot.b_status) === 1;
    if (typeof bot?.status === 'number') return bot.status === 1;
    if (typeof bot?.status === 'string') return bot.status.toLowerCase() === 'active';
    return false;
  };

  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredBots = bots.filter((bot) => {
    const matchesSearch = (bot.name || bot.bot_name || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const isActive = resolveActive(bot);
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && isActive) ||
      (statusFilter === 'inactive' && !isActive);
    return matchesSearch && matchesStatus;
  });

  const tableColumns = [
    { id: 'name', label: 'Bot Name', render: (_, row) => row.name || row.bot_name },
    {
      id: 'status',
      label: 'Status',
      render: (_, row) => {
        const isActive = resolveActive(row);
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: 1.5,
              bgcolor: isActive
                ? (theme) => alpha(theme.palette.success.main, 0.12)
                : (theme) => alpha(theme.palette.grey[500], 0.12),
              color: isActive ? 'success.dark' : 'text.secondary',
              border: 'none',
            }}
          />
        );
      },
    },
    { id: 'blocks', label: 'Blocks', render: (_, row) => row.blocks_count || row.blocks || 0 },
    { id: 'conversations', label: 'Conversations', render: (_, row) => row.conversations || 0 },
    {
      id: 'created',
      label: 'Created',
      render: (_, row) =>
        new Date(row.createdAt || row.created_at || row.bot_created).toLocaleDateString(),
    },
  ];

  return (
    <Box>
      {/* Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
          p: 2,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <TextField
            placeholder="Search bots..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9e9e9e" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.grey[100], 0.8),
                },
                '&.Mui-focused': {
                  bgcolor: 'background.paper',
                },
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'divider',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ bgcolor: 'background.paper', px: 0.5 }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, value) => value && setViewMode(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
                '&:first-of-type': {
                  borderRadius: '8px 0 0 8px !important',
                },
                '&:last-of-type': {
                  borderRadius: '0 8px 8px 0 !important',
                },
              },
            }}
          >
            <ToggleButton value="grid">
              <LayoutGrid size={18} />
            </ToggleButton>
            <ToggleButton value="list">
              <List size={18} />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={onCreateBot}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            Create Bot
          </Button>
        </Box>
      </Paper>

      {/* Empty State */}
      {filteredBots.length === 0 && !loading ? (
        <Paper
          elevation={0}
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: '2px dashed',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Bot size={40} color="#9e9e9e" />
          </Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
            {searchQuery || statusFilter !== 'all' ? 'No bots found' : 'No bots yet'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', maxWidth: 360, mx: 'auto' }}>
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters to find what you\'re looking for'
              : 'Create your first WhatsApp bot to automate conversations and engage with customers'}
          </Typography>
          {!searchQuery && statusFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<Sparkles size={18} />}
              onClick={onCreateBot}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              }}
            >
              Create Your First Bot
            </Button>
          )}
        </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {(loading ? Array.from({ length: 6 }) : filteredBots).map((bot, index) => (
            <Grid item xs={12} sm={6} lg={4} key={bot?.id || bot?.bot_id || index}>
              <BotCard
                bot={bot}
                loading={loading}
                onEdit={onEditBot}
                onDelete={onDeleteBot}
                onSettings={onSettingsBot}
                onView={onViewBot}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <DataTable
            columns={tableColumns}
            data={filteredBots}
            loading={loading}
            onRowClick={onViewBot}
            rowActions={(row) => (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => onEditBot?.(row)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => onDeleteBot?.(row)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                  }}
                >
                  Delete
                </Button>
              </Box>
            )}
          />
        </Paper>
      )}
    </Box>
  );
};

export default BotList;
